import { Trans, useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import { UserAvatarGroup } from '@/components/UserAvatarGroup/UserAvatarGroup';
import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FilterSearch } from '@/design_system/Filters/FiltersMenu/FilterSearch';
import {
  FilterItemNoResults,
  FiltersMenu,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';
import IconSupervisor from '@/icons/Supervisor.svg';
import { useInfiniteUsers } from '@/models/user';
import useDebouncedState from '@/utils/useDebouncedState';

import { useFilters } from '../FiltersState';

const MINIMUM_USERS_FOR_SEARCH = 5;
const USERS_PER_PAGE = 20;

export const FiltersSupervisor = ({ tag }: { tag?: boolean }) => {
  const { t } = useLingui();

  const {
    supervisors: { supervisors, setSupervisors },
  } = useFilters();

  const [search, debouncedSearch, setSearch] = useDebouncedState<string>('', 300);

  // The first hook is to get the total count of users
  // React-query will only fetch once, even with two hooks (as long as the params are the same)
  const { data: initialData } = useInfiniteUsers({
    permissions: ['supervise_request'],
    limit: USERS_PER_PAGE,
  });
  const { data, fetchNextPage, isLoading } = useInfiniteUsers({
    permissions: ['supervise_request'],
    limit: USERS_PER_PAGE,
    search: debouncedSearch || undefined,
  });

  const users = data?.pages.flatMap((page) => page.users) ?? [];
  const count = initialData?.pages[0]?.meta.count || 0;

  const supervisorsIds = supervisors.map(({ id }) => id);

  const options =
    users.length > 0 ? (
      users.map((user) => (
        <FilterCheckboxItem
          key={user.id}
          checked={supervisorsIds.includes(user.id)}
          onChange={(checked) => {
            if (checked) {
              setSupervisors([...supervisors, { id: user.id, name: user.name }]);
            } else {
              setSupervisors(supervisors.filter((supervisor) => supervisor.id !== user.id));
            }
          }}
        >
          <UserAvatar user={user} size="x-small" />
          <span className="text-ellipsis">{user.name}</span>
        </FilterCheckboxItem>
      ))
    ) : (
      <FilterItemNoResults />
    );

  const searchWithOptions = (
    <FilterSearch
      inputProps={{
        ariaLabel: t({
          id: 'requests.filters.supervisor.search.label',
          message: 'Search supervisors',
        }),
        placeholder: t({
          id: 'requests.filters.supervisor.search.label',
          message: 'Search supervisors',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={users.length === 0 && isLoading}
      isLoadingMore={isLoading}
    >
      {options}
    </FilterSearch>
  );

  const selectedSupervisorsCount = supervisors.length;

  const children = count < MINIMUM_USERS_FOR_SEARCH ? options : searchWithOptions;

  if (tag) {
    if (selectedSupervisorsCount === 0) {
      return null;
    }

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={t({ id: 'requests.filters.supervisor.label', message: 'Supervisor' })}
            value={
              selectedSupervisorsCount === 1 ? (
                <>
                  <UserAvatar user={supervisors[0]} size="x-small" />
                  {supervisors[0].name}
                </>
              ) : (
                <>
                  <UserAvatarGroup users={supervisors} size="x-small" withTooltip={false} />
                  <Trans id="requests.filters.supervisor.multiple">
                    {selectedSupervisorsCount} supervisors
                  </Trans>
                </>
              )
            }
            tooltip={
              selectedSupervisorsCount > 1
                ? supervisors.map((user) => user.name).join(', ')
                : undefined
            }
            onClear={() => setSupervisors([])}
          />
        }
      >
        {children}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu
      icon={<IconSupervisor />}
      label={t({ id: 'requests.filters.supervisor.label', message: 'Supervisor' })}
    >
      {children}
    </FiltersSubMenu>
  );
};
